import moment from "moment";
import { notification } from "antd";
import { WDQMS_API, fetchData } from "../../utils/api";
import { variablesSlug } from "../../utils/general";
import * as actionTypes from "./actionTypes";

/**
 * Dictionary for TEMP report types with their supported period types and defaults
 */
export const TEMP_REPORT_TYPES = {
  traditional: {
    periodTypes: ["six_hour", "daily", "monthly"],
    defaultPeriodType: "six_hour",
    url: {
      six_hour: "sixhour-periods",
      daily: "daily-periods",
      monthly: "monthly-periods"
    }
  }
};

/**
 * Sets the period data key
 * @param {Object} data - The data to set
 * @returns {Object} The action object
 */
const setPeriodDataKey = data => {
  return {
    type: actionTypes.SET_PERIOD_DATA_KEY,
    payload: data
  };
};

/**
 * Sets the property
 * @param {Object} data - The data to set
 * @returns {Object} The action object
 */
export const setProperty = data => {
  return { type: actionTypes.SET_PROPERTY, payload: data };
};

/**
 * Updates period types
 * @param {Object} data - The data to update
 * @returns {Object} The action object
 */
export const updatePeriodTypes = data => {
  return {
    type: actionTypes.UPDATE_PERIOD_TYPES,
    payload: data
  };
};

/**
 * Sets error 404
 * @returns {Object} The action object
 */
const setError404 = () => {
  return {
    type: actionTypes.SET_ERROR_404
  };
};

/**
 * Gets the variable ID for a file type and variable
 * @param {string} fileType - The file type
 * @param {string} variable - The variable
 * @returns {string} The variable ID
 */
const getVariableID = (fileType, variable) => {
  return variablesSlug[fileType][variable];
};

/**
 * Checks if an object is empty
 * @param {Object} obj - The object to check
 * @returns {boolean} Whether the object is empty
 */
const isEmpty = obj => {
  return Object.keys(obj).length === 0;
};

let sixHourFetched = false;
let dailyFetched = false;

/**
 * Fetches data from the API for TEMP
 * @param {Object} payload - The payload containing the file type and parameters
 * @param {string} pageType - The page type
 * @returns {Function} A function that takes dispatch and performs the API calls
 */
export const fetchTempDataFromApi = (payload, pageType) => {
  // differentiate between requests coming from the map and from the database status
  if (pageType === "DB_STATUS") {
    pageType = "&page_type=DB_STATUS";
  }
  
  return dispatch => {
    // If we have URL params, set them to the selected params for the menu and map
    if (!isEmpty(payload.params)) {
      dispatch(
        setProperty({
          type: payload.fileType,
          key: "selectedPeriodType",
          value: payload.params.periodType
        })
      );

      if (payload.fileType === "temp") {
        // For TEMP if there is 'payload.params.variable'
        // it means that the report is "quality"
        if (payload.params.variable) {
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedReport",
              value: "quality"
            })
          );
        } else {
          // Otherwise, the report is "availability"
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedReport",
              value: "availability"
            })
          );
        }
      }

      // Update the variable only if it is in the params
      // If not, we keep the default value defined in the store
      // This is necessary for TEMP/Availability as there is no variable in the URL
      if (payload.params.variable) {
        dispatch(
          setProperty({
            type: payload.fileType,
            key: "selectedVariable",
            value: getVariableID(payload.fileType, payload.params.variable)
          })
        );
      }

      dispatch(
        setProperty({
          type: payload.fileType,
          key: "selectedCenter",
          value: payload.params.center
        })
      );

      if (payload.params.center !== "all") {
        dispatch(
          setProperty({
            type: payload.fileType,
            key: "combinedMode",
            value: false
          })
        );
      }

      dispatch(
        setProperty({
          type: payload.fileType,
          key: "selectedDate",
          value: payload.params.date
        })
      );
    }

    if (!payload.alreadyFetched) {
      // Load initial periods of six hour (quick fetch to load page faster)
      if (loadTempInitialPeriods(900, pageType, payload, dispatch)) {
        // Load all periods of six hour
        loadTempAllPeriods(payload, pageType).then(response => {
          // set calendar periods
          setTempCalendarPeriods(response, dispatch, payload, true);
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "allDataFetched",
              value: true
            })
          );
        });
      }
    }
  };
};

/**
 * Load the initial periods of six-hour and daily periods for TEMP
 * @param {number} page_size - The page size for API requests
 * @param {string} pageType - The page type
 * @param {Object} payload - The payload containing file type and parameters
 * @param {Function} dispatch - The dispatch function
 * @returns {boolean} True if successful
 */
function loadTempInitialPeriods(page_size, pageType, payload, dispatch) {
  let date_param = "";
  if (payload.params["date"]) {
    date_param = `&date=${payload.params["date"]}`;
    if (payload.params.periodType === "monthly") {
      // convert date param from YYYY-MM to YYYY-MM-01
      date_param = `&date=${payload.params["date"]}-01`;
    }
  }

  fetchData(
    `${WDQMS_API}/sixhour-periods/?file_type=${payload.fileType}${pageType}&page_size=${page_size}${date_param}`
  )
    .then(response => {
      // response.data.length === 0 -> NO DATA
      if (response.data.length === 0) {
        dispatch(
          setPeriodDataKey({
            subObj: "periodData",
            type: payload.fileType,
            period: "six_hour",
            key: "noData",
            value: true
          })
        );
        // set the data (for a particular fileType) as fetched
        // so that won't be fetched a second time
        dispatch(
          setProperty({
            type: payload.fileType,
            key: "dataFetched",
            value: true
          })
        );
      } else {
        sixHourFetched = true;
        if (isEmpty(payload.params)) {
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedPeriodType",
              value: "six_hour"
            })
          );
        }

        dispatch(
          updatePeriodTypes({
            type: payload.fileType,
            value: "six_hour"
          })
        );
        setTempCalendarPeriods(response.data.results, dispatch, payload, false);

        if (
          isEmpty(payload.params) ||
          payload.params.periodType === "six_hour"
        ) {
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "readyToFillMenu",
              value: true
            })
          );
        }

        // set the data (for a particular fileType) as fetched
        // so that won't be fetched a second time
        dispatch(
          setProperty({
            type: payload.fileType,
            key: "dataFetched",
            value: true
          })
        );
      }
    })
    .catch(error => {
      // If there are URL params requesting the six-hour data
      // and an error while fetching the six-hour data, trigger an error
      console.log("error", error);
      if (
        !isEmpty(payload.params) &&
        payload.params.periodType === "six_hour"
      ) {
        dispatch(setError404());
      }

      sixHourFetched = false;

      notification.error({
        message: "Error fetching API",
        description: "There was an error while fetching the six-hour data",
        top: 100,
        duration: 0
      });
      dispatch(
        setPeriodDataKey({
          subObj: "periodData",
          type: payload.fileType,
          period: "six_hour",
          key: "errorFetchingApi",
          value: true
        })
      );
    })
    .then(response => {
      fetchData(
        `${WDQMS_API}/daily-periods/?file_type=${payload.fileType}${pageType}`
      )
        .then(response => {
          // response.data.length === 0 -> NO DATA
          if (response.data.length === 0) {
            dispatch(
              setPeriodDataKey({
                subObj: "periodData",
                type: payload.fileType,
                period: "daily",
                key: "noData",
                value: true
              })
            );
          } else {
            // if there was an error while fetching the six-hour
            // set the selected period type to "daily"
            dailyFetched = true;
            if (!sixHourFetched) {
              dispatch(
                setProperty({
                  type: payload.fileType,
                  key: "selectedPeriodType",
                  value: "daily"
                })
              );
            }
            
            // file type is temp
            dispatch(
              updatePeriodTypes({
                type: payload.fileType,
                value: ["daily"]
              })
            );

            let tempObj = {};
            for (let p of response.data) {
              if (!tempObj[p.center]) {
                tempObj[p.center] = {};
              }
              tempObj[p.center][moment.utc(p.date).format("YYYY-MM-DD")] = {
                id: p.id
              };
            }
            // The object that contains the dates for the 'combined' version (all centers)
            let objectAll = {};

            // loop through all centers
            for (let ct of Object.keys(tempObj)) {
              // loop through all dates
              for (let d of Object.keys(tempObj[ct])) {
                // if the date is not in arrayAll, add it
                // if objectAll doesn't have the key 'd'
                if (!Object.keys(objectAll).includes(d)) {
                  objectAll[d] = {};
                }
                objectAll[d][ct] = tempObj[ct][d].id;
              }
            }
            // Add objectAll to tempObj
            tempObj.all = objectAll;

            dispatch(
              setPeriodDataKey({
                subObj: "periodData",
                type: payload.fileType,
                period: "daily",
                key: "dates",
                value: tempObj
              })
            );

            dispatch(
              setPeriodDataKey({
                subObj: "periodData",
                type: payload.fileType,
                period: "daily",
                key: "centers",
                value: Object.keys(tempObj).sort(function(a, b) {
                  return a.toLowerCase().localeCompare(b.toLowerCase());
                })
              })
            );

            if (
              !isEmpty(payload.params) &&
              payload.params.periodType === "daily"
            ) {
              dispatch(
                setProperty({
                  type: payload.fileType,
                  key: "selectedCenter",
                  value: payload.params.center
                })
              );

              // if the date doesn't exist for this center,
              // set "selectedDate" to its latest date
              // note: it can happen that a date is available
              // for the six-hour periods but not for the daily
              if (!tempObj[payload.params.center][payload.params.date]) {
                const latestDate = Object.keys(
                  tempObj[payload.params.center]
                ).sort()[
                  Object.keys(tempObj[payload.params.center]).length - 1
                ];

                dispatch(
                  setProperty({
                    type: payload.fileType,
                    key: "selectedDate",
                    value: latestDate
                  })
                );
              } else {
                dispatch(
                  setProperty({
                    type: payload.fileType,
                    key: "selectedDate",
                    value: payload.params.date
                  })
                );
              }
            } else if (!sixHourFetched) {
              // Case no param - this should only be done
              // when there was an error fetching the six-hour data
              const firstCenter = Object.keys(tempObj).sort(function(a, b) {
                return a.toLowerCase().localeCompare(b.toLowerCase());
              })[0];

              dispatch(
                setProperty({
                  type: payload.fileType,
                  key: "selectedCenter",
                  value: firstCenter
                })
              );

              const latestDate = Object.keys(tempObj[firstCenter]).sort()[
                Object.keys(tempObj[firstCenter]).length - 1
              ];

              dispatch(
                setProperty({
                  type: payload.fileType,
                  key: "selectedDate",
                  value: latestDate
                })
              );
            }
            if (
              isEmpty(payload.params) ||
              payload.params.periodType === "daily"
            ) {
              dispatch(
                setProperty({
                  type: payload.fileType,
                  key: "readyToFillMenu",
                  value: true
                })
              );
            }
          }
        })
        .catch(error => {
          // If there are URL params requesting the daily data
          // and an error while fetching the daily data, trigger an error
          console.log(error);
          if (
            !isEmpty(payload.params) &&
            payload.params.periodType === "daily"
          ) {
            dispatch(setError404());
          }
          notification.error({
            message: "Error fetching API",
            description: "There was an error while fetching the daily data",
            top: 100,
            duration: 0
          });
          dispatch(
            setPeriodDataKey({
              subObj: "periodData",
              type: payload.fileType,
              period: "daily",
              key: "errorFetchingApi",
              value: true
            })
          );
        })
        .then(response => {
          fetchData(
            `${WDQMS_API}/monthly-periods/?file_type=${payload.fileType}${pageType}`
          ).then(response => {
            if (response.data.length === 0) {
              dispatch(
                setPeriodDataKey({
                  subObj: "periodData",
                  type: payload.fileType,
                  period: "monthly",
                  key: "noData",
                  value: true
                })
              );
            } else {
              if (!dailyFetched) {
                dispatch(
                  setProperty({
                    type: payload.fileType,
                    key: "selectedPeriodType",
                    value: "monthly"
                  })
                );
              }
              
              // file type is temp
              dispatch(
                updatePeriodTypes({
                  type: payload.fileType,
                  value: ["monthly"]
                })
              );
              
              let tempObj = {};
              for (let p of response.data) {
                if (!tempObj[p.center]) {
                  tempObj[p.center] = {};
                }
                tempObj[p.center][moment.utc(p.date).format("YYYY-MM")] = {
                  id: p.id
                };
              }
              // // The object that contains the dates for the 'combined' version (all centers)
              let objectAll = {};
              // loop through all centers
              for (let ct of Object.keys(tempObj)) {
                // loop through all dates
                for (let d of Object.keys(tempObj[ct])) {
                  // if the date is not in arrayAll, add it
                  // if objectAll doesn't have the key 'd'
                  if (!Object.keys(objectAll).includes(d)) {
                    objectAll[d] = {};
                  }
                  objectAll[d][ct] = tempObj[ct][d].id;
                }
              }
              // Add objectAll to tempObj
              tempObj.all = objectAll;
              dispatch(
                setPeriodDataKey({
                  subObj: "periodData",
                  type: payload.fileType,
                  period: "monthly",
                  key: "dates",
                  value: tempObj
                })
              );
              
              dispatch(
                setPeriodDataKey({
                  subObj: "periodData",
                  type: payload.fileType,
                  period: "monthly",
                  key: "centers",
                  value: Object.keys(tempObj).sort(function(a, b) {
                    return a.toLowerCase().localeCompare(b.toLowerCase());
                  })
                })
              );
              
              if (
                !isEmpty(payload.params) &&
                payload.params.periodType === "monthly"
              ) {
                dispatch(
                  setProperty({
                    type: payload.fileType,
                    key: "selectedCenter",
                    value: payload.params.center
                  })
                );
                // if the date doesn't exist for this center,
                // set "selectedDate" to its latest date
                // note: it can happen that a date is available
                // for the six-hour periods but not for the daily
                if (!tempObj[payload.params.center][payload.params.date]) {
                  const latestDate = Object.keys(
                    tempObj[payload.params.center]
                  ).sort()[
                    Object.keys(tempObj[payload.params.center]).length - 1
                  ];
                  dispatch(
                    setProperty({
                      type: payload.fileType,
                      key: "selectedDate",
                      value: latestDate
                    })
                  );
                } else {
                  dispatch(
                    setProperty({
                      type: payload.fileType,
                      key: "selectedDate",
                      value: payload.params.date
                    })
                  );
                }
              } else if (!dailyFetched) {
                // Case no param - this should only be done
                // when there was an error fetching the six-hour data
                const firstCenter = Object.keys(tempObj).sort(function(a, b) {
                  return a.toLowerCase().localeCompare(b.toLowerCase());
                })[0];

                dispatch(
                  setProperty({
                    type: payload.fileType,
                    key: "selectedCenter",
                    value: firstCenter
                  })
                );

                const latestDate = Object.keys(tempObj[firstCenter]).sort()[
                  Object.keys(tempObj[firstCenter]).length - 1
                ];

                dispatch(
                  setProperty({
                    type: payload.fileType,
                    key: "selectedDate",
                    value: latestDate
                  })
                );
              }
              if (
                isEmpty(payload.params) ||
                payload.params.periodType === "monthly"
              ) {
                dispatch(
                  setProperty({
                    type: payload.fileType,
                    key: "readyToFillMenu",
                    value: true
                  })
                );
              }
            }
          });
        });
    });
  return true;
}

/**
 * Load all six-hour periods asynchronously and return the data for TEMP
 * @param {Object} payload - The payload containing file type and parameters
 * @param {string} pageType - The page type
 * @returns {Promise} A promise that resolves to the fetched data
 */
async function loadTempAllPeriods(payload, pageType) {
  let data = [];
  let nextPage = `${WDQMS_API}/sixhour-periods/?file_type=${payload.fileType}${pageType}&page=1&page_size=8000`;
  while (nextPage) {
    try {
      const response = await fetch(nextPage);
      const jsonResponse = await response.json();
      data = data.concat(jsonResponse.results); // Assuming the API response structure includes a 'results' field
      nextPage = jsonResponse.next; // Update nextPage based on the 'next' field in the response
    } catch (error) {
      console.error("Fetching data error: ", error);
      break; // Exit loop in case of error
    }
  }
  return data;
}

/**
 * Set calendar periods for the six-hour periods for TEMP
 * @param {Array} responseResults - The response results
 * @param {Function} dispatch - The dispatch function
 * @param {Object} payload - The payload containing file type and parameters
 * @param {boolean} allPeriods - Whether all periods should be set
 */
function setTempCalendarPeriods(responseResults, dispatch, payload, allPeriods) {
  if (responseResults) {
    let tempObj = {};
    for (let p of responseResults) {
      if (!tempObj[p.center]) {
        tempObj[p.center] = {};
      }
      if (!tempObj[p.center][moment.utc(p.date).format("YYYY-MM-DD")]) {
        tempObj[p.center][moment.utc(p.date).format("YYYY-MM-DD")] = {
          "00": { available: false, id: null },
          "06": { available: false, id: null },
          "12": { available: false, id: null },
          "18": { available: false, id: null }
        };
      }
      tempObj[p.center][moment.utc(p.date).format("YYYY-MM-DD")][
        moment.utc(p.date).format("HH")
      ].available = true;
      tempObj[p.center][moment.utc(p.date).format("YYYY-MM-DD")][
        moment.utc(p.date).format("HH")
      ].id = p.id;
    }
    // The object that contains the dates for the 'combined' version (all centers)
    let objectAll = {};
    // loop through all centers
    for (let ct of Object.keys(tempObj)) {
      // loop through all dates
      for (let d of Object.keys(tempObj[ct])) {
        // if objectAll doesn't have the key 'd'
        if (!Object.keys(objectAll).includes(d)) {
          objectAll[d] = {};
          // loop through the 4 six-hour periods
          for (let p of Object.keys(tempObj[ct][d])) {
            if (tempObj[ct][d][p].available) {
              objectAll[d][p] = {};
              objectAll[d][p].available = true;
              objectAll[d][p].id = {};
              objectAll[d][p].id[ct] = tempObj[ct][d][p].id;
            } else {
              objectAll[d][p] = {
                available: false,
                id: {}
              };
            }
          }
        } else {
          for (let p of Object.keys(tempObj[ct][d])) {
            if (tempObj[ct][d][p].available) {
              objectAll[d][p].available = true;
              objectAll[d][p].id[ct] = tempObj[ct][d][p].id;
            }
          }
        }
      }
    }
    tempObj.all = objectAll;
    dispatch(
      setPeriodDataKey({
        subObj: "periodData",
        type: payload.fileType,
        period: "six_hour",
        key: "dates",
        value: tempObj
      })
    );
    dispatch(
      setPeriodDataKey({
        subObj: "periodData",
        type: payload.fileType,
        period: "six_hour",
        key: "centers",
        // case insensitive sort so that "all" is first element
        value: Object.keys(tempObj).sort(function(a, b) {
          return a.toLowerCase().localeCompare(b.toLowerCase());
        })
      })
    );
    if (!allPeriods) {
      let latestDate;
      let availablePeriods;
      if (isEmpty(payload.params)) {
        const firstCenter = Object.keys(tempObj).sort(function(a, b) {
          return a.toLowerCase().localeCompare(b.toLowerCase());
        })[0];
        dispatch(
          setProperty({
            type: payload.fileType,
            key: "selectedCenter",
            value: firstCenter
          })
        );

        latestDate = Object.keys(tempObj[firstCenter]).sort()[
          Object.keys(tempObj[firstCenter]).length - 1
        ];
        dispatch(
          setProperty({
            type: payload.fileType,
            key: "selectedDate",
            value: latestDate
          })
        );
        availablePeriods = tempObj[firstCenter][latestDate];

        const arrAvailablePeriods = Object.keys(availablePeriods)
          .sort()
          .filter(p => availablePeriods[p].available);

        dispatch(
          setProperty({
            type: payload.fileType,
            key: "selectedSixHPeriod",
            value: arrAvailablePeriods[arrAvailablePeriods.length - 1]
          })
        );
      } else if (payload.params.periodType === "six_hour") {
        latestDate = payload.params.date;
        // check if the date exist
        if (tempObj[payload.params.center][latestDate]) {
          availablePeriods = tempObj[payload.params.center][latestDate];

          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedSixHPeriod",
              value: payload.params.period
            })
          );

          // if the date doesn't exist, trigger an error
        } else {
          dispatch(setError404());
        }

        // if the six-hour period is not available for this date,
        // also trigger an error
        if (
          tempObj[payload.params.center][latestDate] &&
          typeof payload.params.period !== "undefined"
        ) {
          if (!availablePeriods[payload.params.period].available) {
            dispatch(setError404());
          }
        }
      }
      // if (!allPeriods) {
      dispatch(
        setProperty({
          type: payload.fileType,
          key: "currentSixHPeriods",
          value: availablePeriods
        })
      );
    }
  }
}
